import { booleanAttribute } from "#ce/utils/props";

const elevationOptions = ["1", "2", "3"];

export const props = Object.freeze({
  /**
   * Permanently fix the toolbar to the top of the viewport, independent of scroll position.
   *
   * @type {Boolean|String}
   * @default false
   */
  sticky: booleanAttribute(),
  /**
   * Stick to the top of the viewport once the toolbar would otherwise scroll off-screen. Uses an IntersectionObserver against a sentinel element placed in front of the toolbar. Ignored when sticky is true.
   *
   * @type {Boolean|String}
   * @default false
   */
  stickyOnScroll: booleanAttribute(),
  /**
   * Position the toolbar absolutely against its nearest positioned ancestor (top: 0, left: 0, right: 0).
   *
   * @type {Boolean|String}
   * @default false
   */
  absolute: booleanAttribute(),
  /**
   * Top offset (in pixels) applied when the toolbar is sticky. Use this to leave room for a separate site header that is already pinned above.
   *
   * @type {Number|String}
   * @default 0
   * @storybook-control number
   */
  offset: {
    type: [Number, String],
    default: 0,
  },
  /**
   * CSS selector for an element whose measured height should be used as the offset. Wins over the numeric offset prop when both are set.
   *
   * @type {String}
   * @default undefined
   */
  offsetSelector: {
    type: String,
    default: undefined,
  },
  /**
   * Continuously track the offsetSelector element's live bottom edge instead of a one-time measurement, and use it for the sticky top position — plus, on sticky-on-scroll toolbars, the stick/unstick boundary and the anchor scroll padding (a permanently sticky toolbar manages neither). Use for site headers that grow or shrink on scroll (e.g. a header that reveals a utility bar on scroll-up), so the sticky toolbar keeps pace with the header, sticks at the right moment, and anchor targets land below it. Requires offsetSelector; has no effect on its own.
   *
   * @type {Boolean|String}
   * @default false
   */
  dynamicOffset: booleanAttribute(),
  /**
   * Background colour treatment. Currently only "white" maps to a defined surface; other values are passed through as a --color-<value> modifier class.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  color: {
    type: String,
    default: undefined,
  },
  /**
   * Drop shadow strength. "1" is subtle, "3" is most prominent.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  elevation: {
    type: String,
    default: undefined,
    validator: (value) => elevationOptions.includes(value),
  },
});
